import React, { useState, useCallback, useRef } from 'react';

/**
 * DepthControl — 3-way segmented control for progressive-disclosure depth.
 * ─────────────────────────────────────────────────────────────────────────────
 * Drives the page-level "detail level" that every ProgressiveCard /
 * BentoResults on the page reads. Works controlled (pass `depth` + `onChange`)
 * or uncontrolled (omit `depth`; seeded from `defaultDepth`, owns its own
 * state). Either way `onChange` fires on every segment change.
 *
 * Mobile: position:sticky to the top of the results on scroll (never fixed).
 * ≤520px: trimmed labels. Styles: .vc-depth in public/design/v2/site.css. The
 * sticky wrapper must sit directly in the tall results column, or it has no
 * room to stick.
 *
 * Props:
 *   depth          — controlled value 1|2|3. If undefined ⇒ uncontrolled.
 *   defaultDepth   — uncontrolled seed (default 1)
 *   onChange       — (next:1|2|3) => void
 *   stickyOnMobile — apply sticky behaviour on mobile (default true)
 * ─────────────────────────────────────────────────────────────────────────
 */

const SEGMENTS = [
  { depth: 1, full: 'Just the numbers', trim: 'Just numbers' },
  { depth: 2, full: "What's behind them", trim: "What's behind" },
  { depth: 3, full: 'Show the maths', trim: 'The maths' },
];

export default function DepthControl({
  depth,
  defaultDepth = 1,
  onChange,
  stickyOnMobile = true,
}) {
  const [internal, setInternal] = useState(defaultDepth);
  const isControlled = depth != null;
  const value = isControlled ? depth : internal;
  const btnRefs = useRef([]);

  const select = useCallback(
    (next) => {
      if (!isControlled) setInternal(next);
      if (onChange) onChange(next);
    },
    [isControlled, onChange],
  );

  const onKeyDown = useCallback(
    (e) => {
      const idx = SEGMENTS.findIndex((s) => s.depth === value);
      let nextIdx = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        nextIdx = Math.min(SEGMENTS.length - 1, idx + 1);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        nextIdx = Math.max(0, idx - 1);
      } else if (e.key === 'Home') {
        nextIdx = 0;
      } else if (e.key === 'End') {
        nextIdx = SEGMENTS.length - 1;
      }
      if (nextIdx == null) return;
      e.preventDefault();
      const nextDepth = SEGMENTS[nextIdx].depth;
      select(nextDepth);
      const node = btnRefs.current[nextIdx];
      if (node) node.focus();
    },
    [value, select],
  );

  return (
    <div className={`vc-depth-wrap${stickyOnMobile ? ' vc-depth-wrap--sticky' : ''}`}>
      <div
        className="vc-depth"
        role="group"
        aria-label="Detail level"
        onKeyDown={onKeyDown}
      >
        {SEGMENTS.map((s, i) => {
          const active = s.depth === value;
          return (
            <button
              key={s.depth}
              ref={(n) => {
                btnRefs.current[i] = n;
              }}
              type="button"
              aria-pressed={active}
              tabIndex={active ? 0 : -1}
              onClick={() => select(s.depth)}
            >
              <span className="vc-depth__full">{s.full}</span>
              <span className="vc-depth__trim">{s.trim}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
